import { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

type ResponseType = 'success' | 'failure' | 'none'

export default function ContactForm() {
  const nameField = useRef<HTMLInputElement>(null)
  const emailField = useRef<HTMLInputElement>(null)
  const messageField = useRef<HTMLTextAreaElement>(null)

  const [responseType, setResponseType] = useState<ResponseType>('none')
  const [responseMessage, setResponseMessage] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const data = {
      name: nameField.current?.value ?? '',
      email: emailField.current?.value ?? '',
      message: messageField.current?.value ?? '',
    }
    fetch('/api/contact', {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then((res) => {
      setIsSubmitting(false)
      if (res.status === 200) {
        setResponseType('success')
        setResponseMessage(
          'Thanks for submitting! Our experts will be in touch with you shortly.'
        )
        if (nameField.current) nameField.current.value = ''
        if (emailField.current) emailField.current.value = ''
        if (messageField.current) messageField.current.value = ''
      } else {
        setResponseType('failure')
        setResponseMessage(
          'Something went wrong. You can email <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> instead.'
        )
      }
    })
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay: 0.1 }}
      className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
    >
      <div className="space-y-5">
        <div>
          <label
            htmlFor="contact-name"
            className="block text-body-sm font-medium text-ink-primary"
          >
            Name <span className="text-ink-tertiary">(required)</span>
          </label>
          <input
            id="contact-name"
            ref={nameField}
            type="text"
            name="name"
            required
            minLength={2}
            placeholder="Jane Doe"
            autoComplete="name"
            className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-body-sm font-medium text-ink-primary"
          >
            Email <span className="text-ink-tertiary">(required)</span>
          </label>
          <input
            id="contact-email"
            ref={emailField}
            type="email"
            name="email"
            required
            placeholder="you@your-company.com"
            autoComplete="email"
            className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <div>
          <label
            htmlFor="contact-message"
            className="block text-body-sm font-medium text-ink-primary"
          >
            Message
          </label>
          <textarea
            id="contact-message"
            ref={messageField}
            name="message"
            rows={5}
            placeholder="Your inquiries and anything you want to know…"
            className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        {responseMessage && (
          <div
            className={`rounded-xl px-4 py-3 text-body-sm ${
              responseType === 'success'
                ? 'bg-accent-50 text-accent-800'
                : 'bg-[#fef2f2] text-[#b91c1c]'
            }`}
            dangerouslySetInnerHTML={{ __html: responseMessage }}
          />
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex w-full items-center justify-center rounded-pill bg-brand-900 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Sending…' : 'Send message →'}
        </button>
        <Text size="sm" tone="tertiary" className="text-center">
          We respond within one business day.
        </Text>
      </div>
    </motion.form>
  )
}
